import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useRef, useState } from 'react';
import { ChevronDown } from 'react-feather';

import { QueryResponseType } from '@/__generated__/graphql';

const OPTIONS = [
  { value: QueryResponseType.YesNo, display: 'Yes / No' },
  { value: QueryResponseType.FreeForm, display: 'Free-form' },
];

const labelFor = (value: QueryResponseType): string =>
  OPTIONS.find((o) => o.value === value)?.display ?? 'Free-form';

interface ResponseTypeSelectProps {
  id: string;
  value: QueryResponseType;
  readOnly?: boolean;
  onChange: (value: QueryResponseType) => void;
}

/** Footer control selecting how an expert answers a vetting query (ENG-5292). */
export function ResponseTypeSelect({
  id,
  value,
  readOnly,
  onChange,
}: ResponseTypeSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <span className="flex items-center gap-6 sm:gap-8">
      <span className="shrink-0 whitespace-nowrap text-12 font-semibold text-on-surface-secondary sm:text-14">
        Response type
      </span>
      {readOnly ? (
        <span className="text-14 font-semibold text-on-surface-secondary">{labelFor(value)}</span>
      ) : (
        <>
          <button
            ref={anchorRef}
            type="button"
            id={`response-type-${id}`}
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            className="flex min-w-[6rem] items-center justify-center gap-6 rounded border border-surface-border bg-surface-card px-8 py-4 text-12 font-semibold text-on-surface sm:min-w-[7rem]"
          >
            {labelFor(value)}
            <ChevronDown
              className={`h-12 w-12 shrink-0 text-on-surface-subtle transition-transform ${
                open ? 'rotate-180' : ''
              }`}
            />
          </button>
          <Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen(false)}>
            {OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.display}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </span>
  );
}
